import type {
  FileUploadForm,
  KnowledgeCollection,
  KnowledgeQueryResult,
  KnowledgeSchemaType,
} from "@/schemas/knowledge.schema";
import type { RequestConfig } from "@/services/api";
import { api } from "@/services/api";

export const knowledgeApi = {
  async get(config?: RequestConfig) {
    const { data } = await api<Omit<KnowledgeCollection, "documents">[]>("/knowledge", config);

    return data;
  },

  async getCollection(id: string, config?: RequestConfig) {
    const { data } = await api<KnowledgeCollection>(`/knowledge/${id}`, config);

    return data;
  },

  async createCollection(
    name: string,
    body: Pick<KnowledgeCollection, "description" | "model_name"> & { schema?: { properties: KnowledgeSchemaType } },
    config?: RequestConfig
  ) {
    const { data } = await api.post<{ id: string; name: string }>(`/knowledge/${name}`, body, config);
    return data;
  },

  async updateCollection(
    id: string,
    body: Pick<KnowledgeCollection, "description" | "model_name">,
    config?: RequestConfig
  ) {
    await api.patch(`/knowledge/${id}`, body, config);
  },

  async addToCollection(
    id: string,
    documents: Record<string, any>[],
    config?: RequestConfig
  ) {
    const { data } = await api.post(`/knowledge/${id}/add`, documents, config);

    return data;
  },

  async syncKnowledge(
    id: string,
    body: { documents: any[]; model?: string; schema?: KnowledgeSchemaType },
    config?: RequestConfig
  ) {
    await api.post(`/knowledge/${id}/sync`, body, config);
  },

  async uploadFile(
    {
      id,
      file,
      model,
      options,
    }: {
      id: string;
      file: File;
      model?: string;
      options: FileUploadForm["options"];
    },
    config?: RequestConfig
  ) {
    const formData = new FormData();
    formData.append("file", file);

    for (const [key, value] of Object.entries(options)) {
      formData.append(key, String(value));
    }

    await api.post(`/knowledge/${id}/file`, formData, {
      ...config,
      params: {
        model,
      },
    });
  },

  async query(
    id: string,
    body: { query: string; n?: number },
    config?: RequestConfig
  ) {
    const { data } = await api.post<KnowledgeQueryResult[]>(
      `/knowledge/${id}/query`,
      body,
      config
    );

    return data;
  },

  async removeCollection(id: string, config?: RequestConfig) {
    await api.delete(`/knowledge/${id}`, config);
  },
};
